import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Chip,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Stack,
  LinearProgress,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  ContentCopy as CopyIcon,
  FolderOpen as FolderOpenIcon,
  Cancel as CancelIcon,
  Science as CalibratedIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { buildQualityRows } from '../../utils/qualityMetrics';
import axios from 'axios';
import { apiService } from '../../services/apiService';
import { formatSize, withAuthToken, filePreviewUrl } from '../../utils/fileUtils';
import { useAuth } from '../../contexts/AuthContext';
import FileCalibrationChip from '../../components/files/FileCalibrationChip';
import ProcessingFailureChip from '../../components/files/ProcessingFailureChip';
import ConfirmDialog from '../../components/ConfirmDialog';
import LazyImage from '../../components/common/LazyImage';
import PushExternallyDialog from '../../components/PushExternallyDialog';
import ImageSelectionBar from '../../components/files/ImageSelectionBar';
import ZipDownloadButton from '../../components/files/ZipDownloadButton';
import { getAccessToken, getRefreshToken, updateAccessToken } from '../../services/authStorage';
import { useImageSelection } from '../../hooks/useImageSelection';
import { isUncalibratedResponse, markUncalibratedName, buildUncalibratedWarning } from '../../utils/calibratedDownload';

// Helper function to format dates in UTC with 24-hour time
function formatUTC(dateString, formatStr = 'PPP HH:mm:ss') {
  if (!dateString) return 'N/A';
  try {
    let utcDateString = dateString;
    if (!dateString.endsWith('Z') && !dateString.match(/[+-]\d{2}:\d{2}$/)) {
      utcDateString = dateString + 'Z';
    }
    return formatInTimeZone(parseISO(utcDateString), 'UTC', formatStr) + ' UTC';
  } catch (error) {
    return dateString;
  }
}

// Convert decimal degrees to sexagesimal format
function decimalToSexagesimal(decimal, isRA = false) {
  if (decimal === null || decimal === undefined) return 'N/A';
  const absolute = Math.abs(decimal);
  if (isRA) {
    const hours = absolute / 15;
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    const s = ((hours - h) * 60 - m) * 60;
    return `${h.toString().padStart(2, '0')}h ${m.toString().padStart(2, '0')}m ${s.toFixed(2).padStart(5, '0')}s`;
  } else {
    const sign = decimal < 0 ? '-' : '+';
    const d = Math.floor(absolute);
    const m = Math.floor((absolute - d) * 60);
    const s = ((absolute - d) * 60 - m) * 60;
    return `${sign}${d.toString().padStart(2, '0')}° ${m.toString().padStart(2, '0')}' ${s.toFixed(1).padStart(4, '0')}"`;
  }
}

const ObservationFiles = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'server_admin' || user?.role === 'admin' || user?.role === 'instructor';

  // Capture incoming state for back navigation
  const fromState = location.state;

  const handleBack = () => {
    if (fromState?.from === 'projects') {
      // Navigate back to projects with state to restore expanded accordions
      navigate('/projects', { state: fromState });
    } else {
      navigate(-1);
    }
  };

  const [observation, setObservation] = useState(null);
  const [files, setFiles] = useState([]);
  // RAW-only count/bytes + the server's effective ZIP caps (#686) for precise
  // ZIP-button gating (the file list reports file_type:'fits' for every row).
  const [zipCaps, setZipCaps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Transient errors from per-file downloads — shown as a Snackbar so a
  // single failure doesn't replace the whole page like the fatal-load error.
  const [downloadError, setDownloadError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailReqRef = useRef(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({
    status: 'idle', // idle | fetching | downloading | complete | error | cancelled
    current: 0,
    total: 0,
    filename: '',
    error: ''
  });
  const downloadAbortController = useRef(null);
  const sessionKeepAliveInterval = useRef(null);

  // Check if File System Access API is supported
  const supportsDirectoryPicker = 'showDirectoryPicker' in window;

  // Multi-select subset download is folder-picker-only (see #1275): saving many files
  // any other way prompts the browser once per file. Gate the whole feature on it.
  const multiSelect = supportsDirectoryPicker;
  const sel = useImageSelection(files);

  // Esc clears the multi-select (Chrome-only feature).
  useEffect(() => {
    if (!multiSelect) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') sel.clear(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [multiSelect, sel.clear]);

  // Detect if browser is Chrome (has "keep" prompt issue for multiple downloads)
  const isChrome = /Chrome/.test(navigator.userAgent) && !/Edg|OPR/.test(navigator.userAgent);

  // Session keep-alive during long downloads to prevent token expiration
  const startSessionKeepAlive = () => {
    if (sessionKeepAliveInterval.current) return;
    console.log('[Download] Starting session keep-alive');

    const refreshSession = async () => {
      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) return;

        const response = await fetch('/api/v1/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            updateAccessToken(data.data.accessToken, data.data.refreshToken);
            console.log('[Download] Token refreshed successfully');
          }
        }
      } catch (error) {
        console.warn('[Download] Token refresh error:', error.message);
      }
    };

    refreshSession();
    sessionKeepAliveInterval.current = setInterval(refreshSession, 5 * 60 * 1000);
  };

  const stopSessionKeepAlive = () => {
    if (sessionKeepAliveInterval.current) {
      console.log('[Download] Stopping session keep-alive');
      clearInterval(sessionKeepAliveInterval.current);
      sessionKeepAliveInterval.current = null;
    }
  };

  // Cleanup keep-alive on unmount
  useEffect(() => {
    return () => stopSessionKeepAlive();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch observation and files in parallel
        const [obsResponse, filesResponse] = await Promise.all([
          apiService.getObservation(id),
          apiService.getUserFiles({ observationId: id }),
        ]);

        setObservation(obsResponse.data || obsResponse);
        setFiles(filesResponse.data?.files || filesResponse.files || []);
        {
          const d = filesResponse.data || {};
          setZipCaps({
            // If the server sent counts but no 'raw' key, that's a genuine 0 raw frames
            // (disable the button); if it sent no counts at all, leave null = unknown so
            // the button falls back to the full file count rather than disabling.
            rawCount: d.file_type_counts ? (d.file_type_counts.raw ?? 0) : null,
            rawBytes: d.file_type_bytes?.raw ?? 0,
            maxFiles: d.zip_limits?.max_files,
            maxBytes: d.zip_limits?.max_bytes,
          });
        }
      } catch (err) {
        console.error('Error fetching observation files:', err);
        setError(err.message || 'Failed to load files');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
      case 'assigned':
        return 'primary';
      case 'pending':
        return 'default';
      case 'failed':
        return 'error';
      case 'cancelled':
      case 'paused':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTotalSize = () => {
    const total = files.reduce((sum, file) => sum + (file.file_size || 0), 0);
    return formatSize(total);
  };

  const [calibratingFileId, setCalibratingFileId] = useState(null);
  const [pushDialogOpen, setPushDialogOpen] = useState(false);

  const handleDownload = async (file, format = 'raw') => {
    try {
      if (format === 'calibrated' && file.file_id) {
        // Single GET to the transparent proxy (serves the stored calibrated artifact
        // or calibrates on demand); on !ok fall back to the legacy on-demand route.
        // One request means the calibration runs once — the old HEAD+navigate ran a
        // full calibration on the HEAD AND again on the navigated GET, and a HEAD that
        // merely matched masters could be followed by a GET whose calibration failed,
        // navigating the browser to a raw 404 body. Fetch+blob mirrors the raw path
        // below (#1276/#1277).
        setCalibratingFileId(file.file_id);
        try {
          const proxyUrl = withAuthToken(`/api/v1/fits/download-proxy-calibrated/${encodeURIComponent(file.file_id)}`);
          let response = await fetch(proxyUrl, { credentials: 'same-origin' });
          if (!response.ok) {
            // Fallback: legacy on-demand calibration route.
            const token = getAccessToken();
            response = await fetch(`/api/v1/calibration/download/${file.file_id}?format=calibrated&token=${encodeURIComponent(token)}`);
            if (!response.ok) {
              const err = await response.json().catch(() => ({}));
              throw new Error(err.error || 'Calibration failed');
            }
          }
          const blob = await response.blob();
          const contentDisposition = response.headers.get('Content-Disposition');
          let filename = file.filename?.replace(/\.(fits|fit)(\.fz)?$/i, '_calibrated.fits.fz') || 'calibrated.fits.fz';
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
            if (match) filename = match[1];
          }
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        } finally {
          setCalibratingFileId(null);
        }
        return;
      }
      // Use file_path with the proxy endpoint (same as bulk download)
      const filePath = file.file_path || file.file_id;
      if (!filePath) {
        setError('File path not available');
        return;
      }
      const filename = file.filename || filePath.split('/').pop();
      const downloadUrl = withAuthToken(`/api/v1/fits/download-proxy/${encodeURIComponent(filePath)}?filename=${encodeURIComponent(filename)}`);
      // Fetch through the proxy so we can surface server-side errors (e.g.
      // file deleted from external storage) instead of opening a blank window
      // when the browser navigates to a 404/500 JSON body.
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Download failed (${response.status})`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download file:', err);
      setDownloadError(err.message || 'Failed to download file. Please try again.');
    }
  };

  // Cancel ongoing download
  const handleCancelDownload = () => {
    if (downloadAbortController.current) {
      downloadAbortController.current.abort();
      downloadAbortController.current = null;
    }
    stopSessionKeepAlive();
    setDownloadProgress({ status: 'cancelled', current: downloadProgress.current, total: downloadProgress.total, filename: '', error: '' });
  };

  // Fallback download for browsers without File System Access API
  const handleFallbackDownload = async () => {
    if (isChrome && !window.isSecureContext) {
      alert(
        'Bulk "Download All to Folder" requires a secure (HTTPS) connection in Chrome.\n\n' +
        'Please access this site via HTTPS to enable this feature, ' +
        'or download files individually using the download buttons on each file card.'
      );
      return;
    }

    startSessionKeepAlive();
    try {
      setDownloadProgress({ status: 'fetching', current: 0, total: 0, filename: '', error: '' });

      const token = getAccessToken();
      const response = await fetch(`/api/v1/observations/${id}/download-urls`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        if (response.status === 401) throw new Error('Session expired — please log in again');
        throw new Error(`Failed to get download URLs (${response.status})`);
      }
      const result = await response.json();
      const data = result.data;

      if (data.files.length === 0) {
        stopSessionKeepAlive();
        setDownloadProgress({ status: 'complete', current: 0, total: 0, filename: '', error: '' });
        return;
      }

      // Download first file via fetch to measure bandwidth
      const firstFile = data.files[0];
      setDownloadProgress({
        status: 'downloading',
        current: 1,
        total: data.files.length,
        filename: `${firstFile.filename} (measuring speed...)`,
        error: ''
      });

      const startTime = performance.now();
      // firstFile.url is a server-minted download-proxy URL (now authenticated);
      // attach the token since a header-less fetch can't.
      const firstResponse = await fetch(withAuthToken(firstFile.url), { credentials: 'same-origin' });
      if (!firstResponse.ok) {
        throw new Error(`Failed to download first file: ${firstResponse.status}`);
      }
      const firstBlob = await firstResponse.blob();
      const endTime = performance.now();

      const downloadTimeMs = endTime - startTime;
      const bytesPerMs = firstBlob.size / downloadTimeMs;

      // Trigger download of first file
      const blobUrl = URL.createObjectURL(firstBlob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = firstFile.filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      if (data.files.length === 1) {
        stopSessionKeepAlive();
        setDownloadProgress({ status: 'complete', current: 1, total: 1, filename: '', error: '' });
        return;
      }

      // Download remaining files
      for (let i = 1; i < data.files.length; i++) {
        const file = data.files[i];

        setDownloadProgress({
          status: 'downloading',
          current: i + 1,
          total: data.files.length,
          filename: file.filename,
          error: ''
        });

        const fileLink = document.createElement('a');
        fileLink.href = file.url;
        fileLink.download = file.filename;
        fileLink.style.display = 'none';
        document.body.appendChild(fileLink);
        fileLink.click();
        document.body.removeChild(fileLink);

        const estimatedTimeMs = (file.size || firstBlob.size) / bytesPerMs * 1.2;
        const delayMs = Math.ceil(estimatedTimeMs);

        setDownloadProgress({
          status: 'downloading',
          current: i + 1,
          total: data.files.length,
          filename: `${file.filename} - waiting ${Math.round(delayMs / 1000)}s...`,
          error: ''
        });

        await new Promise(resolve => setTimeout(resolve, delayMs));
      }

      stopSessionKeepAlive();
      setDownloadProgress({ status: 'complete', current: data.files.length, total: data.files.length, filename: '', error: '' });
    } catch (error) {
      stopSessionKeepAlive();
      console.error('Fallback download failed:', error);
      setDownloadProgress({ status: 'error', current: 0, total: 0, filename: '', error: error.message || 'Download failed' });
    }
  };

  // File System Access API download handler with parallel downloads.
  // selectedIds (a Set of file_ids) limits the batch to a chosen subset (#1275);
  // null downloads every file. Only ever called with a subset from the Chromium-only
  // multi-select bar, so the fallback branch below never carries a subset.
  const handleDownloadAll = async (format = 'raw', selectedIds = null) => {
    if (!supportsDirectoryPicker) {
      await handleFallbackDownload();
      return;
    }

    downloadAbortController.current = new AbortController();
    const signal = downloadAbortController.current.signal;
    const CONCURRENCY_LIMIT = format === 'calibrated' ? 2 : 4;

    // Show immediate feedback before directory picker
    setDownloadProgress({ status: 'fetching', current: 0, total: 0, filename: 'Select a folder...', error: '' });

    let dirHandle;
    try {
      dirHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'downloads'
      });
    } catch (err) {
      // User cancelled the directory picker
      setDownloadProgress({ status: 'idle', current: 0, total: 0, filename: '', error: '' });
      return;
    }

    try {
      startSessionKeepAlive();
      setDownloadProgress({ status: 'fetching', current: 0, total: 0, filename: 'Getting file URLs...', error: '' });
      // Allow React to render
      await new Promise(resolve => setTimeout(resolve, 50));

      const token = getAccessToken();
      const response = await fetch(`/api/v1/observations/${id}/download-urls`, {
        signal,
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        if (response.status === 401) throw new Error('Session expired — please log in again');
        throw new Error(`Failed to get download URLs (${response.status})`);
      }
      const result = await response.json();
      const data = result.data;
      // #1275: a subset selection downloads only those file_ids; otherwise all files.
      const files = selectedIds ? data.files.filter(f => selectedIds.has(f.file_id)) : data.files;

      setDownloadProgress({ status: 'downloading', current: 0, total: files.length, filename: 'Starting downloads...', error: '' });
      // Allow React to render
      await new Promise(resolve => setTimeout(resolve, 50));

      let completedCount = 0;
      // #1276: frames the server couldn't calibrate come back raw (X-Calibration-Status:
      // unavailable) — collect them so we can warn once the batch finishes.
      const uncalibrated = [];

      const downloadFile = async (file, index) => {
        if (signal.aborted) {
          throw new DOMException('Download cancelled', 'AbortError');
        }

        try {
          // For calibrated downloads, use the calibration endpoint per file
          // format=both returns calibrated if available, raw otherwise
          // file.url is a server-minted download-proxy URL (now authenticated);
          // attach the token for the header-less fetch below.
          let downloadUrl = withAuthToken(file.url);
          if (format === 'calibrated' && file.file_id) {
            const token = getAccessToken();
            downloadUrl = `/api/v1/calibration/download/${file.file_id}?format=both&token=${encodeURIComponent(token)}`;
          }

          const fileResponse = await fetch(downloadUrl, { credentials: 'same-origin', signal });
          if (!fileResponse.ok) {
            console.error(`Failed to download ${file.filename}: ${fileResponse.status}`);
            return false;
          }

          const blob = await fileResponse.blob();

          // Use filename from Content-Disposition if available (calibrated files get _calibrated suffix)
          let filename = file.filename;
          const contentDisposition = fileResponse.headers.get('Content-Disposition');
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
            if (match) filename = match[1];
          }

          // #1276: a `format=both` request returns the RAW frame with
          // X-Calibration-Status: unavailable when it can't be calibrated. Never save
          // that under a calibrated-looking name — mark it and warn once at the end.
          if (format === 'calibrated'
              && isUncalibratedResponse(fileResponse.headers.get('X-Calibration-Status'))) {
            uncalibrated.push(filename);
            filename = markUncalibratedName(filename);
          }

          const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();

          return true;
        } catch (err) {
          if (err.name === 'AbortError') throw err;
          console.error(`Error with ${file.filename}:`, err);
          return false;
        }
      };

      for (let i = 0; i < files.length; i += CONCURRENCY_LIMIT) {
        if (signal.aborted) {
          stopSessionKeepAlive();
          setDownloadProgress({ status: 'cancelled', current: completedCount, total: files.length, filename: '', error: '' });
          return;
        }

        const batch = files.slice(i, i + CONCURRENCY_LIMIT);
        const batchNames = batch.map(f => f.filename.split('.')[0]).join(', ');

        setDownloadProgress({
          status: 'downloading',
          current: completedCount,
          total: files.length,
          filename: `Downloading: ${batchNames}...`,
          error: ''
        });
        // Allow React to render progress update
        await new Promise(resolve => setTimeout(resolve, 50));

        try {
          const results = await Promise.all(
            batch.map((file, batchIndex) => downloadFile(file, i + batchIndex))
          );
          completedCount += results.filter(Boolean).length;

          setDownloadProgress({
            status: 'downloading',
            current: completedCount,
            total: files.length,
            filename: `${completedCount} of ${files.length} completed...`,
            error: ''
          });
          // Allow React to render progress update
          await new Promise(resolve => setTimeout(resolve, 50));
        } catch (err) {
          if (err.name === 'AbortError') {
            stopSessionKeepAlive();
            setDownloadProgress({ status: 'cancelled', current: completedCount, total: files.length, filename: '', error: '' });
            return;
          }
          console.error('Batch error:', err);
        }
      }

      setDownloadProgress({ status: 'complete', current: completedCount, total: files.length, filename: '', error: '' });
      const uncalibratedWarning = buildUncalibratedWarning(uncalibrated);
      if (uncalibratedWarning) setDownloadError(uncalibratedWarning);
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadProgress({ status: 'error', current: 0, total: 0, filename: '', error: error.message || 'Download failed' });
    } finally {
      stopSessionKeepAlive();
      downloadAbortController.current = null;
    }
  };

  const handleViewMetadata = async (file) => {
    // Open immediately with the lean list item; heavy fields
    // (fits_header/quality_metrics/plate_solve_result) are fetched separately.
    setSelectedFile(file);
    setMetadataDialogOpen(true);
    const fid = file.file_id || file._id;
    detailReqRef.current = fid;
    if (!fid) return;
    setDetailLoading(true);
    let d = {};
    try {
      const resp = await apiService.getUserFile(id, fid);
      d = resp?.data || {};
    } catch (_) {
      // keep basic view on failure
    }
    if (detailReqRef.current !== fid) return; // a newer file was opened
    setSelectedFile(prev => (prev && (prev.file_id || prev._id) === fid)
      ? { ...prev, fits_header: d.fits_header, quality_metrics: d.quality_metrics, plate_solve_result: d.plate_solve_result }
      : prev);
    setDetailLoading(false);
  };

  const handleViewPreview = (file) => {
    setSelectedFile(file);
    setPreviewDialogOpen(true);
  };

  const handleDeleteFile = (file) => {
    // Prefer _id (MongoDB ObjectId/UUID) over file_id (which may have fits_ prefix)
    const fileId = file._id || file.file_id;
    const fileName = file.file_name || file.filename || file.file_path?.split('/').pop();
    setConfirm({
      title: 'Delete File',
      message: <span>Are you sure you want to delete <strong>{fileName}</strong>?</span>,
      confirmLabel: 'Delete',
      confirmColor: 'error',
      confirmIcon: <DeleteIcon />,
      icon: <DeleteIcon color="error" />,
      onConfirm: async () => {
        try {
          const token = getAccessToken();
          const response = await axios.delete(`/api/v1/user-files/${fileId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setFiles(prev => prev.filter(f => (f.file_id || f._id) !== fileId));
          if (response.data?.observation_deleted) {
            navigate(-1);
          }
        } catch (err) {
          setDownloadError('Failed to delete file');
          throw err;
        }
      }
    });
  };

  const canDeleteFile = (file) => {
    return isAdmin || file.user_id === user?._id;
  };

  const handleCopyPath = async (path) => {
    try {
      await navigator.clipboard.writeText(path);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getPreviewUrl = (file) => {
    // Cache-bust on preview_updated_at (only moves when the PNG is actually
    // (re)written) rather than updated_at (moves on every UserFile save, e.g.
    // plate-solve/quality-metrics). Using updated_at churned the URL during
    // rebuild polling and re-downloaded every preview each tick (#925). Matches
    // the pattern already used by ObservationDetail.js (#614).
    // #1287: cache-stable signed URL (token fallback when the server didn't sign).
    if (file.preview_url) {
      return filePreviewUrl(file);
    }
    if (file.thumbnail_url) {
      return filePreviewUrl(file, { thumbnail: true });
    }
    return null;
  };

  const targetName = observation?.target?.name || observation?.name || 'Observation';

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box mt={4} mb={4}>
          <Box display="flex" alignItems="center" mb={3}>
            <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
            <Skeleton variant="text" width={300} height={40} />
          </Box>
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Card>
                  <Skeleton variant="rectangular" height={140} />
                  <CardContent>
                    <Skeleton variant="text" />
                    <Skeleton variant="text" width="60%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="lg">
        <Box mt={4}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
          <Box mt={2}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => handleBack()}>
              Go Back
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  // Empty state
  if (files.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box mt={4} mb={4}>
          {/* Header */}
          <Box display="flex" alignItems="center" mb={3}>
            <IconButton onClick={() => handleBack()} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box flexGrow={1}>
              <Typography variant="h5">{targetName}</Typography>
              <Typography variant="body2" color="text.secondary">
                Observation Files
              </Typography>
            </Box>
            {observation?.status && (
              <Chip
                label={observation.status}
                color={getStatusColor(observation.status)}
                sx={{ textTransform: 'capitalize' }}
              />
            )}
          </Box>

          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <FileIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No files uploaded for this observation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Files will appear here once the observation captures data
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box mt={4} mb={4}>
        {/* Header */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
            <IconButton onClick={() => handleBack()}>
              <ArrowBackIcon />
            </IconButton>
            <Box flexGrow={1}>
              <Typography variant="h5">{targetName}</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                {observation?.status && (
                  <Chip
                    label={observation.status}
                    color={getStatusColor(observation.status)}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                )}
                <Typography variant="body2" color="text.secondary">
                  {files.length} file{files.length !== 1 ? 's' : ''} • {getTotalSize()}
                </Typography>
              </Stack>
            </Box>
            {downloadProgress.status === 'idle' && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {supportsDirectoryPicker && (
                  <Button
                    variant="outlined"
                    startIcon={<FolderOpenIcon />}
                    onClick={() => handleDownloadAll('raw')}
                  >
                    Download Raw
                  </Button>
                )}
                {/* #686: raw ZIP offered in every browser (works over plain HTTP —
                    no secure-context requirement), beside the folder picker where
                    it exists. */}
                <ZipDownloadButton
                  obsId={id}
                  format="raw"
                  fileCount={zipCaps?.rawCount ?? files.length}
                  totalBytes={zipCaps?.rawBytes ?? 0}
                  maxFiles={zipCaps?.maxFiles}
                  maxBytes={zipCaps?.maxBytes}
                  label="Download Raw (zip)"
                  icon={<DownloadIcon />}
                />
                {(() => {
                  const calibratableCount = files.filter(f => f.calibratable).length;
                  if (calibratableCount === 0) return null;
                  return (
                    <>
                      {supportsDirectoryPicker && (
                        <Tooltip title={`${calibratableCount} of ${files.length} files have calibration masters available. Others will download as raw.`}>
                          <Button
                            variant="outlined"
                            color="success"
                            startIcon={<FolderOpenIcon />}
                            onClick={() => handleDownloadAll('calibrated')}
                          >
                            Download Calibrated ({calibratableCount}/{files.length})
                          </Button>
                        </Tooltip>
                      )}
                      {/* #686: calibrated streaming ZIP in every browser too. */}
                      <ZipDownloadButton
                        obsId={id}
                        format="calibrated"
                        fileCount={zipCaps?.rawCount ?? files.length}
                        totalBytes={zipCaps?.rawBytes ?? 0}
                        maxFiles={zipCaps?.maxFiles}
                        maxBytes={zipCaps?.maxBytes}
                        label="Download Calibrated (zip)"
                        icon={<CalibratedIcon />}
                        color="success"
                      />
                    </>
                  );
                })()}
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => setPushDialogOpen(true)}
                  disabled={files.length === 0}
                >
                  Push Externally
                </Button>
              </Box>
            )}
            {(downloadProgress.status === 'downloading' || downloadProgress.status === 'fetching') && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={handleCancelDownload}
              >
                Cancel
              </Button>
            )}
          </Box>

          {/* Download Progress */}
          {downloadProgress.status !== 'idle' && (
            <Box sx={{ mt: 2 }}>
              {downloadProgress.status === 'fetching' && (
                <Alert severity="info">
                  <Typography variant="body2">Preparing download...</Typography>
                  <LinearProgress sx={{ mt: 1 }} />
                </Alert>
              )}
              {downloadProgress.status === 'downloading' && (
                <Alert severity="info">
                  <Typography variant="body2">
                    Downloading {downloadProgress.current} of {downloadProgress.total} files
                    {downloadProgress.filename && ` - ${downloadProgress.filename}`}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(downloadProgress.current / downloadProgress.total) * 100}
                    sx={{ mt: 1 }}
                  />
                </Alert>
              )}
              {downloadProgress.status === 'complete' && (
                <Alert
                  severity="success"
                  onClose={() => setDownloadProgress({ status: 'idle', current: 0, total: 0, filename: '', error: '' })}
                >
                  Download complete! {downloadProgress.total} file{downloadProgress.total !== 1 ? 's' : ''} downloaded.
                </Alert>
              )}
              {downloadProgress.status === 'cancelled' && (
                <Alert
                  severity="warning"
                  onClose={() => setDownloadProgress({ status: 'idle', current: 0, total: 0, filename: '', error: '' })}
                >
                  Download cancelled. {downloadProgress.current} of {downloadProgress.total} files downloaded.
                </Alert>
              )}
              {downloadProgress.status === 'error' && (
                <Alert
                  severity="error"
                  onClose={() => setDownloadProgress({ status: 'idle', current: 0, total: 0, filename: '', error: '' })}
                >
                  Download failed: {downloadProgress.error}
                </Alert>
              )}
            </Box>
          )}
        </Paper>

        {multiSelect && (
          <ImageSelectionBar
            sel={sel}
            busy={downloadProgress.status === 'fetching' || downloadProgress.status === 'downloading'}
            onDownloadRaw={() => handleDownloadAll('raw', sel.selectedIds)}
            onDownloadCalibrated={() => handleDownloadAll('calibrated', sel.calibratableSelectedIds)}
          />
        )}

        {/* File Grid */}
        <Grid container spacing={2}>
          {files.map((file, index) => {
            const previewUrl = getPreviewUrl(file);
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={file.file_id || file.file_path || index}>
                <Card
                  sx={{
                    height: '100%', display: 'flex', flexDirection: 'column', position: 'relative',
                    ...(multiSelect && sel.isSelected(file.file_id) && {
                      outline: '2px solid', outlineColor: 'primary.main',
                    }),
                  }}
                >
                  {multiSelect && sel.isSelected(file.file_id) && (
                    <CheckCircleIcon
                      color="primary"
                      sx={{ position: 'absolute', top: 4, left: 4, zIndex: 2, bgcolor: 'background.paper', borderRadius: '50%' }}
                    />
                  )}
                  {/* Preview + metadata is the selection click target (Chrome-only multi-select) */}
                  <Box
                    onClick={multiSelect ? (e) => sel.handleCardClick(index, e) : undefined}
                    sx={multiSelect ? { cursor: 'pointer', userSelect: 'none' } : undefined}
                  >
                  {/* Preview Image or Placeholder */}
                  {previewUrl ? (
                    <Box sx={{ position: 'relative' }}>
                      <LazyImage
                        // Lazy-load: only fetch the preview when the card nears
                        // the viewport (#925). previewUrl carries a
                        // preview_updated_at cache-bust, so a genuinely rebuilt
                        // preview changes the src and repaints without any
                        // remount (preserves #614); an unrelated updated_at
                        // change no longer refetches.
                        src={previewUrl}
                        height={140}
                        alt={file.filename}
                        sx={{ objectFit: 'cover' }}
                        onClick={multiSelect ? undefined : () => handleViewPreview(file)}
                      />
                      <Box sx={{ position: 'absolute', top: 4, right: 4, opacity: 0.9 }}>
                        <FileCalibrationChip file={file} />
                        <ProcessingFailureChip file={file} />
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        height: 140,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.100',
                        cursor: 'pointer',
                      }}
                      onClick={multiSelect ? undefined : () => handleViewMetadata(file)}
                    >
                      <FileIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Tooltip title={file.filename || 'Unknown'}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ fontWeight: 500, mb: 0.5 }}
                      >
                        {file.filename || 'Unknown'}
                      </Typography>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formatSize(file.file_size)} • {formatDate(file.captured_at)}
                    </Typography>
                    {(file.filter && file.filter !== 'N/A') || file.metadata?.extracted?.filter ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Filter: {file.metadata?.extracted?.filter || file.filter}
                        {file.exposure_time ? ` • ${file.exposure_time}s` : ''}
                      </Typography>
                    ) : null}
                    {(() => {
                      const gain = file.metadata?.extracted?.gain ?? file.metadata?.header?.EGAIN ?? file.metadata?.header?.GAIN;
                      return gain !== undefined && gain !== null ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Gain: {gain}
                        </Typography>
                      ) : null;
                    })()}
                  </CardContent>
                  </Box>

                  <CardActions sx={{ pt: 0 }}>
                    <Tooltip title="Download Raw">
                      <IconButton size="small" onClick={() => handleDownload(file)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {file.calibratable && (
                      <Tooltip title={calibratingFileId === (file.file_id || file.file_path) ? 'Calibrating...' : 'Download Calibrated'}>
                        <IconButton
                          size="small"
                          color="success"
                          disabled={!!calibratingFileId}
                          onClick={() => handleDownload(file, 'calibrated')}
                        >
                          {calibratingFileId === (file.file_id || file.file_path)
                            ? <CircularProgress size={16} color="success" />
                            : <CalibratedIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="View Info">
                      <IconButton size="small" onClick={() => handleViewMetadata(file)}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {previewUrl && (
                      <Tooltip title="View Preview">
                        <IconButton size="small" onClick={() => handleViewPreview(file)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDeleteFile(file) && (
                      <Tooltip title="Delete File">
                        <IconButton size="small" color="error" onClick={() => handleDeleteFile(file)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Metadata Dialog */}
        <Dialog
          open={metadataDialogOpen}
          onClose={() => setMetadataDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center">
              <InfoIcon sx={{ mr: 1 }} />
              File Details
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedFile && (
              <Box>
                {/* Basic Information */}
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '30%' }}>
                          Filename
                        </TableCell>
                        <TableCell>{selectedFile.filename || 'Unknown'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          File Size
                        </TableCell>
                        <TableCell>{formatSize(selectedFile.file_size)}</TableCell>
                      </TableRow>
                      {(selectedFile.fits_header || selectedFile.filter) && (
                        <>
                          {(selectedFile.fits_header?.OBJECT || selectedFile.target_name) && (
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                Object
                              </TableCell>
                              <TableCell>{selectedFile.fits_header?.OBJECT || selectedFile.target_name}</TableCell>
                            </TableRow>
                          )}
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Filter
                            </TableCell>
                            <TableCell>
                              <Chip label={selectedFile.filter || selectedFile.fits_header?.FILTER || 'Unknown'} size="small" color="primary" />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Exposure Time
                            </TableCell>
                            <TableCell>{(selectedFile.exposure_time || selectedFile.fits_header?.EXPTIME) ? `${selectedFile.exposure_time || selectedFile.fits_header?.EXPTIME} seconds` : 'Unknown'}</TableCell>
                          </TableRow>
                          {(selectedFile.binning || selectedFile.fits_header?.XBINNING) && (
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                Binning
                              </TableCell>
                              <TableCell>{selectedFile.binning || `${selectedFile.fits_header?.XBINNING}x${selectedFile.fits_header?.YBINNING || selectedFile.fits_header?.XBINNING}`}</TableCell>
                            </TableRow>
                          )}
                          {(selectedFile.image_type || selectedFile.fits_header?.IMAGETYP) && (
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                Image Type
                              </TableCell>
                              <TableCell>{selectedFile.image_type || selectedFile.fits_header?.IMAGETYP}</TableCell>
                            </TableRow>
                          )}
                          {(selectedFile.target_ra != null || selectedFile.fits_header?.RA != null) &&
                           (selectedFile.target_dec != null || selectedFile.fits_header?.DEC != null) && (
                            <>
                              <TableRow>
                                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                  Coordinates (RA/Dec)
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>
                                  {decimalToSexagesimal(selectedFile.target_ra ?? selectedFile.fits_header?.RA, true)} / {decimalToSexagesimal(selectedFile.target_dec ?? selectedFile.fits_header?.DEC, false)}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                  Coordinates (Decimal)
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                                  {(selectedFile.target_ra ?? selectedFile.fits_header?.RA)?.toFixed(6)}&deg; / {(selectedFile.target_dec ?? selectedFile.fits_header?.DEC)?.toFixed(6)}&deg;
                                </TableCell>
                              </TableRow>
                            </>
                          )}
                          {selectedFile.fits_header?.['DATE-OBS'] && (
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                Observation Date
                              </TableCell>
                              <TableCell>{formatUTC(selectedFile.fits_header['DATE-OBS'])}</TableCell>
                            </TableRow>
                          )}
                          {selectedFile.fits_header?.AIRMASS && (
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                Airmass
                              </TableCell>
                              <TableCell>{Number(selectedFile.fits_header.AIRMASS).toFixed(3)}</TableCell>
                            </TableRow>
                          )}
                          {selectedFile.fits_header?.TELESCOP && (
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                Telescope
                              </TableCell>
                              <TableCell>{selectedFile.fits_header.TELESCOP}</TableCell>
                            </TableRow>
                          )}
                          {(selectedFile.camera_name || selectedFile.fits_header?.INSTRUME) && (
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                Instrument
                              </TableCell>
                              <TableCell>{selectedFile.camera_name || selectedFile.fits_header?.INSTRUME}</TableCell>
                            </TableRow>
                          )}
                          {selectedFile.fits_header?.OBSERVER && (
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                Observer
                              </TableCell>
                              <TableCell>{selectedFile.fits_header.OBSERVER}</TableCell>
                            </TableRow>
                          )}
                        </>
                      )}
                      {selectedFile.checksum && (
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                            Checksum
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                            {selectedFile.checksum}
                          </TableCell>
                        </TableRow>
                      )}
                      {selectedFile.uploaded_at && (
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                            Uploaded
                          </TableCell>
                          <TableCell>{formatUTC(selectedFile.uploaded_at)}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Detail (fits_header/quality_metrics) is fetched on open */}
                {detailLoading && !selectedFile.fits_header && !selectedFile.quality_metrics && (
                  <Box display="flex" alignItems="center" gap={1} sx={{ mb: 3 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">Loading header and quality data…</Typography>
                  </Box>
                )}

                {/* Quality Metrics */}
                {selectedFile.quality_metrics && (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                      Image Quality Metrics
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                      <Table size="small">
                        <TableBody>
                          {buildQualityRows(selectedFile.quality_metrics).map(([label, value]) => (
                            <TableRow key={label}>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '40%' }}>
                                {label}
                              </TableCell>
                              <TableCell>{value}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {/* FITS Header Data */}
                {selectedFile.fits_header && Object.keys(selectedFile.fits_header).length > 0 && (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                      FITS Header Data
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableBody>
                          {Object.entries(selectedFile.fits_header)
                            .filter(([key]) => {
                              const skipKeys = ['FILTER', 'EXPTIME', 'EXPOSURE', 'OBJECT', 'INSTRUME',
                                              'TELESCOP', 'XBINNING', 'YBINNING', 'RA', 'DEC', 'AIRMASS'];
                              return !skipKeys.includes(key.toUpperCase());
                            })
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([key, value]) => (
                            <TableRow key={key}>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '30%', fontFamily: 'monospace' }}>
                                {key}
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                                {typeof value === 'number' ? value.toFixed(6).replace(/\.?0+$/, '') : String(value)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {/* Storage Information */}
                {selectedFile.file_path && (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                      Storage Information
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '30%' }}>
                              Storage Path
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                    wordBreak: 'break-all',
                                  }}
                                >
                                  {selectedFile.file_path}
                                </Typography>
                                <Tooltip title={copySuccess ? 'Copied!' : 'Copy path'}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCopyPath(selectedFile.file_path)}
                                  >
                                    <CopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMetadataDialogOpen(false)}>Close</Button>
            {selectedFile && (
              <>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    handleDownload(selectedFile);
                    setMetadataDialogOpen(false);
                  }}
                >
                  Download Raw
                </Button>
                {selectedFile.calibratable && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CalibratedIcon />}
                    onClick={() => {
                      handleDownload(selectedFile, 'calibrated');
                      setMetadataDialogOpen(false);
                    }}
                  >
                    Download Calibrated
                  </Button>
                )}
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {selectedFile?.filename || 'Preview'}
          </DialogTitle>
          <DialogContent>
            {selectedFile && getPreviewUrl(selectedFile) && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: 'grey.900',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <img
                  src={getPreviewUrl(selectedFile)}
                  alt={selectedFile.filename}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            )}
            {selectedFile && !getPreviewUrl(selectedFile) && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 4,
                }}
              >
                <ImageIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">
                  No preview available for this file
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
            {selectedFile && (
              <>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    handleDownload(selectedFile);
                    setPreviewDialogOpen(false);
                  }}
                >
                  Download Raw
                </Button>
                {selectedFile.calibratable && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CalibratedIcon />}
                    onClick={() => {
                      handleDownload(selectedFile, 'calibrated');
                      setPreviewDialogOpen(false);
                    }}
                  >
                    Download Calibrated
                  </Button>
                )}
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Transient download error — keeps the page visible behind it,
            unlike the fatal-load `error` state which replaces the page. */}
        <Snackbar
          open={!!downloadError}
          autoHideDuration={6000}
          onClose={() => setDownloadError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity="error"
            onClose={() => setDownloadError(null)}
            sx={{ width: '100%' }}
          >
            {downloadError}
          </Alert>
        </Snackbar>

        <ConfirmDialog
          open={Boolean(confirm)}
          title={confirm?.title}
          message={confirm?.message}
          confirmLabel={confirm?.confirmLabel}
          confirmColor={confirm?.confirmColor}
          confirmIcon={confirm?.confirmIcon}
          icon={confirm?.icon}
          onConfirm={confirm?.onConfirm}
          onClose={() => setConfirm(null)}
        />

        <PushExternallyDialog
          open={pushDialogOpen}
          onClose={() => setPushDialogOpen(false)}
          observationId={id}
        />
      </Box>
    </Container>
  );
};

export default ObservationFiles;
